import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Shield, FileText, CheckCircle2, Lock, AlertTriangle, ExternalLink } from 'lucide-react';

interface LegalStatus {
  accepted: boolean;
  currentVersion: string;
  acceptedVersion?: string;
  acceptedAt?: string;
}

export const LegalConsentModal: React.FC = () => {
  const { user, token } = useAuth();
  const [legalStatus, setLegalStatus] = useState<LegalStatus | null>(null);
  const [activeTab, setActiveTab] = useState<'privacy' | 'terms' | 'cookies'>('privacy');
  const [policies, setPolicies] = useState<any>(null);
  const [checkPrivacy, setCheckPrivacy] = useState(false);
  const [checkTerms, setCheckTerms] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);

  // Verificar estado de aceptación cuando el usuario está logueado
  useEffect(() => {
    if (!token || !user) {
      setLegalStatus(null);
      return;
    }

    const checkStatus = async () => {
      try {
        setLoading(true);
        const res = await api.get('/legal/status');
        setLegalStatus(res.data);
        if (!res.data.accepted) {
          // Cargar los textos completos de las políticas
          const polRes = await api.get('/legal/policies');
          setPolicies(polRes.data);
        }
      } catch (err) {
        console.error('Error al consultar estado legal:', err);
      } finally {
        setLoading(false);
      }
    };

    checkStatus();
  }, [token, user]);

  // Si no está autenticado o ya aceptó, no renderizar nada
  if (!token || !user || !legalStatus || legalStatus.accepted) {
    return null;
  }

  const handleAcceptPolicies = async () => {
    if (!checkPrivacy || !checkTerms) {
      toast.error('Debes marcar las dos casillas de aceptación para continuar.');
      return;
    }

    try {
      setIsSubmitting(true);
      const res = await api.post('/legal/accept');
      if (res.data?.success) {
        toast.success('¡Políticas aceptadas y registradas con plena validez legal (Ley 1581 de 2012)!', {
          duration: 5000,
          icon: '🛡️'
        });
        setLegalStatus({
          accepted: true,
          currentVersion: res.data.currentVersion || 'v1.0-2026-10',
          acceptedAt: res.data.acceptedAt
        });
      }
    } catch (err: any) {
      console.error('Error al registrar aceptación:', err);
      toast.error(err.response?.data?.error || 'No se pudo registrar la aceptación. Intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 999999,
      background: 'rgba(15, 23, 42, 0.88)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
      fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif"
    }}>
      <div style={{
        maxWidth: '820px',
        width: '100%',
        maxHeight: '92vh',
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 25px 60px -15px rgba(0, 0, 0, 0.5)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: '1.5px solid #00df82'
      }}>
        {/* Header con Identidad Oficial */}
        <div style={{
          background: 'linear-gradient(135deg, #063c3d 0%, #042526 100%)',
          padding: '24px 28px',
          color: '#ffffff',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'rgba(0, 223, 130, 0.15)',
                border: '1.5px solid #00df82',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Shield size={26} color="#00df82" />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '800', letterSpacing: '-0.02em' }}>
                    Aceptación Obligatoria de Políticas y Privacidad
                  </h2>
                  <span style={{
                    background: 'rgba(0, 223, 130, 0.2)',
                    color: '#00df82',
                    border: '1px solid rgba(0, 223, 130, 0.4)',
                    fontSize: '10.5px',
                    fontWeight: '800',
                    padding: '2px 8px',
                    borderRadius: '6px'
                  }}>
                    {legalStatus.currentVersion}
                  </span>
                </div>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', opacity: 0.85 }}>
                  Pentágono Constructora • Condominio Cielito Lindo 2 • Nexo CRM
                </p>
              </div>
            </div>

            <div style={{
              background: 'rgba(239, 68, 68, 0.18)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#fca5a5',
              padding: '6px 12px',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Lock size={13} /> Requerido para operar en la plataforma
            </div>
          </div>
        </div>

        {/* Pestañas de Navegación de Políticas */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          background: '#f8fafc',
          padding: '0 20px'
        }}>
          <button
            type="button"
            onClick={() => setActiveTab('privacy')}
            style={{
              padding: '14px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'privacy' ? '3px solid #063c3d' : '3px solid transparent',
              color: activeTab === 'privacy' ? '#063c3d' : '#64748b',
              fontWeight: activeTab === 'privacy' ? '800' : '600',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Shield size={15} color={activeTab === 'privacy' ? '#063c3d' : '#64748b'} />
            Tratamiento de Datos (Ley 1581)
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('terms')}
            style={{
              padding: '14px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'terms' ? '3px solid #063c3d' : '3px solid transparent',
              color: activeTab === 'terms' ? '#063c3d' : '#64748b',
              fontWeight: activeTab === 'terms' ? '800' : '600',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <FileText size={15} color={activeTab === 'terms' ? '#063c3d' : '#64748b'} />
            Términos y Confidencialidad
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('cookies')}
            style={{
              padding: '14px 18px',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'cookies' ? '3px solid #063c3d' : '3px solid transparent',
              color: activeTab === 'cookies' ? '#063c3d' : '#64748b',
              fontWeight: activeTab === 'cookies' ? '800' : '600',
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <span>🍪</span> Política de Cookies
          </button>
        </div>

        {/* Contenido con Scroll de la Política Seleccionada */}
        <div style={{
          flex: '1 1 0',
          minHeight: 0,
          overflowY: 'auto',
          padding: '24px 28px',
          background: '#ffffff',
          color: '#1e293b',
          fontSize: '13px',
          lineHeight: '1.65'
        }}>
          {activeTab === 'privacy' && (
            <div>
              <div style={{
                background: '#ecfdf5',
                border: '1.5px solid #a7f3d0',
                borderRadius: '12px',
                padding: '14px 16px',
                marginBottom: '18px'
              }}>
                <strong style={{ color: '#065f46', fontSize: '13.5px', display: 'block', marginBottom: '4px' }}>
                  Cumplimiento Estricto de la Ley Estatutaria 1581 de 2012 y Decreto 1377 de 2013
                </strong>
                <p style={{ margin: 0, fontSize: '12px', color: '#047857' }}>
                  <strong>Responsable:</strong> PENTAGONOARENITAS S.A.S. (NIT 901.419.588-7), Representante Legal: Ricardo Alfonso Ferro Lozano (C.C. 80.471.816). Proyecto Condominio Campestre Cielito Lindo 2.
                </p>
              </div>

              <h3 style={{ color: '#063c3d', fontSize: '15px', fontWeight: '800', marginTop: 0 }}>
                1. Finalidades Principales del Tratamiento
              </h3>
              <ul style={{ paddingLeft: '20px', margin: '8px 0 16px 0' }}>
                <li>Gestión comercial inmobiliaria y asesoría personalizada de lotes urbanizados.</li>
                <li>Proyección, firma digitalizada vinculante y ejecución de contratos de Promesa de Compraventa.</li>
                <li>Seguimiento de cartera, acuerdos de financiación directa (36 meses) y recaudos.</li>
                <li>Comunicaciones oficiales vía WhatsApp, correo electrónico y llamadas con trazabilidad probatoria (Ley 527 de 1999).</li>
              </ul>

              <h3 style={{ color: '#063c3d', fontSize: '15px', fontWeight: '800' }}>
                2. Derechos del Titular (Derechos ARCO)
              </h3>
              <p>
                Como usuario o titular de los datos, tienes derecho a conocer, actualizar, rectificar y solicitar la supresión de tu información personal, así como revocar la autorización otorgada a través del canal oficial: <strong>datos@pentagonoarenitas.com</strong>.
              </p>
            </div>
          )}

          {activeTab === 'terms' && (
            <div>
              <div style={{
                background: '#fffbeb',
                border: '1.5px solid #fde68a',
                borderRadius: '12px',
                padding: '14px 16px',
                marginBottom: '18px'
              }}>
                <strong style={{ color: '#92400e', fontSize: '13.5px', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                  <AlertTriangle size={16} /> Cláusula de Confidencialidad y Secreto Comercial
                </strong>
                <p style={{ margin: 0, fontSize: '12px', color: '#b45309' }}>
                  Toda la información de clientes, precios, contratos y bases de datos alojada en Nexo CRM es propiedad confidencial exclusiva de Pentágono Constructora. Queda terminantemente prohibida su extracción, copia o divulgación a terceros.
                </p>
              </div>

              <h3 style={{ color: '#063c3d', fontSize: '15px', fontWeight: '800', marginTop: 0 }}>
                1. Obligaciones del Usuario del CRM
              </h3>
              <ul style={{ paddingLeft: '20px', margin: '8px 0 16px 0' }}>
                <li>Custodiar diligentemente sus credenciales de acceso personales e intransferibles.</li>
                <li>Utilizar la información de los prospectos únicamente para la gestión comercial autorizada.</li>
                <li>La sustracción no autorizada de bases de datos constituye delito tipificado en la Ley 1273 de 2009 de Colombia.</li>
              </ul>
            </div>
          )}

          {activeTab === 'cookies' && (
            <div>
              <h3 style={{ color: '#063c3d', fontSize: '15px', fontWeight: '800', marginTop: 0 }}>
                Cookies Técnicas y Almacenamiento Local
              </h3>
              <p>
                Nexo CRM utiliza almacenamiento técnico estrictamente indispensable para:
              </p>
              <ul style={{ paddingLeft: '20px', margin: '8px 0 16px 0' }}>
                <li>Mantener activa tu sesión de forma segura mediante tokens cifrados JWT.</li>
                <li>Preservar tus preferencias de navegación en el mapa de lotes y contratos.</li>
                <li>Almacenar la constancia criptográfica de aceptación de estas políticas.</li>
              </ul>
              <p style={{ fontSize: '12px', color: '#64748b' }}>
                Nexo CRM no comercializa datos de navegación con terceros ni utiliza cookies publicitarias invasivas.
              </p>
            </div>
          )}
        </div>

        {/* Footer Obligatorio con Checkboxes y Botón de Confirmación */}
        <div style={{
          padding: '20px 28px',
          background: '#f8fafc',
          borderTop: '1.5px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px'
        }}>
          {/* Checkbox 1: Política de Datos */}
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: 'pointer',
            fontSize: '12.5px',
            fontWeight: '600',
            color: '#0f172a'
          }}>
            <input
              type="checkbox"
              checked={checkPrivacy}
              onChange={(e) => setCheckPrivacy(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                marginTop: '2px',
                accentColor: '#063c3d',
                cursor: 'pointer'
              }}
            />
            <span>
              He leído, comprendo y acepto la <strong>Política de Tratamiento y Protección de Datos Personales</strong> de Pentágono Constructora bajo la Ley 1581 de 2012 y autorizo el uso de mis datos para los fines indicados.
            </span>
          </label>

          {/* Checkbox 2: Términos y Cookies */}
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            cursor: 'pointer',
            fontSize: '12.5px',
            fontWeight: '600',
            color: '#0f172a'
          }}>
            <input
              type="checkbox"
              checked={checkTerms}
              onChange={(e) => setCheckTerms(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                marginTop: '2px',
                accentColor: '#063c3d',
                cursor: 'pointer'
              }}
            />
            <span>
              Acepto los <strong>Términos y Condiciones de Uso</strong>, la estricta reserva de confidencialidad comercial de la información de clientes y lotes, y la Política de Cookies de Nexo CRM.
            </span>
          </label>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginTop: '6px' }}>
            <span style={{ fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Shield size={13} color="#00df82" />
              Se registrará tu IP, dispositivo y fecha con Hash SHA-256 de validez legal.
            </span>

            <button
              type="button"
              disabled={!checkPrivacy || !checkTerms || isSubmitting}
              onClick={handleAcceptPolicies}
              style={{
                padding: '12px 24px',
                borderRadius: '10px',
                background: (!checkPrivacy || !checkTerms || isSubmitting) ? '#94a3b8' : '#063c3d',
                color: '#ffffff',
                border: 'none',
                fontSize: '13px',
                fontWeight: '800',
                cursor: (!checkPrivacy || !checkTerms || isSubmitting) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: (!checkPrivacy || !checkTerms) ? 'none' : '0 4px 14px rgba(6, 60, 61, 0.3)',
                transition: 'all 0.15s ease'
              }}
            >
              {isSubmitting ? (
                <>Registrando Aceptación...</>
              ) : (
                <>
                  <CheckCircle2 size={16} color="#00df82" />
                  Aceptar Políticas y Desbloquear Acceso
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegalConsentModal;
